import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { styleFor, incidentDivIcon, incidentPopupHtml, prioritiseIncidents } from "@/lib/incidents";
import { vanMarkerImageUrl, vanDivHtml, vehiclePopupHtml, buildRegSet, matchesRegSet } from "@/lib/liveVehicles";
import { useLiveVehicles } from "@/hooks/useLiveVehicles";
import LiveVehiclesToggle from "@/components/LiveVehiclesToggle";
import MapLegend from "@/components/MapLegend";

const TRANSPARENT_PX = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const WRAP = "w-full h-[420px] lg:h-[560px] rounded-xl overflow-hidden border border-slate-200 relative z-0";

// Real Google Maps JS map (live TrafficLayer) when a browser key is set,
// otherwise the keyless Leaflet demo map. Switches automatically on key add.
export default function TaskRouteMap(props) {
  const [failReason, setFailReason] = useState(null);
  const debug = typeof window !== "undefined" && window.localStorage && localStorage.getItem("htss_maps_debug") === "1";
  if (MAPS_KEY && !failReason) return <GoogleTrafficMap {...props} onFail={(why) => setFailReason(why || "unknown")} />;
  if (failReason && debug) {
    return (
      <div className={props.className || WRAP} data-testid="task-route-map-error"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "#fef2f2", color: "#b91c1c", padding: 16, textAlign: "center", whiteSpace: "pre-wrap", fontSize: 13 }}>
        {`Google Maps failed to initialise\nReason: ${failReason}`}
      </div>
    );
  }
  return <LeafletRouteMap {...props} />;
}

// ---------------------------------------------------------------------------
// Google Maps JS (imperative via js-api-loader) — markers + route + traffic
// ---------------------------------------------------------------------------
function GoogleTrafficMap({ route, className, incidents, showAll = false, onShowAllChange, providers, onFail, restrictRegistrations }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const trafficRef = useRef(null);
  const objsRef = useRef([]);
  const incRef = useRef([]);
  const vehRef = useRef([]);
  const vehInfoRef = useRef(null);
  const infoRef = useRef(null);
  const roRef = useRef(null);
  const boundsRef = useRef(null);
  const [showTraffic, setShowTraffic] = useState(true);
  const [ready, setReady] = useState(false);
  const { vehicles, enabled: vansOn, available: vansAvail } = useLiveVehicles();
  const _regSet = buildRegSet(restrictRegistrations);
  const _all = (vansOn && vansAvail) ? vehicles : [];
  const liveVehicles = _regSet ? _all.filter((v) => matchesRegSet(v.registration, _regSet)) : _all;
  const prioritised = prioritiseIncidents(incidents || [], { cap: 12, showAll });

  useEffect(() => {
    let cancelled = false;
    setOptions({ key: MAPS_KEY, v: "weekly" });
    const fail = (why) => { if (!cancelled && onFail) onFail(why || "authentication error (invalid key / RefererNotAllowed / billing / quota)"); };
    // Google calls window.gm_authFailure on any key/billing/referrer error.
    // Fan out to EVERY mounted map so a later mount can't clobber earlier handlers.
    if (!window.__gmFailCbs) { window.__gmFailCbs = new Set(); window.gm_authFailure = () => window.__gmFailCbs.forEach((f) => f()); }
    window.__gmFailCbs.add(fail);
    // If the API never finishes loading (network/JS error), fall back too.
    const timer = setTimeout(() => { if (!cancelled && !(window.google && window.google.maps)) fail("Maps JS API did not load within 8s (network / CSP / script blocked)"); }, 8000);
    Promise.all([importLibrary("maps"), importLibrary("marker")]).then(() => {
      if (cancelled || !ref.current || !window.google) return;
      clearTimeout(timer);
      const google = window.google;
      const map = new google.maps.Map(ref.current, {
        center: { lat: 53.8, lng: -2.5 }, zoom: 6,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false, clickableIcons: false,
      });
      mapRef.current = map;
      trafficRef.current = new google.maps.TrafficLayer();
      setReady(true);
      // Slide-over/animated containers can size to 0 at init → grey map.
      // Re-trigger a resize + refit whenever the container's size changes.
      if (ref.current && "ResizeObserver" in window) {
        const ro = new ResizeObserver(() => {
          if (!mapRef.current) return;
          google.maps.event.trigger(mapRef.current, "resize");
          if (boundsRef.current && !boundsRef.current.isEmpty()) mapRef.current.fitBounds(boundsRef.current, 48);
        });
        ro.observe(ref.current);
        roRef.current = ro;
      }
    }).catch((e) => { clearTimeout(timer); fail("importLibrary error: " + (e && e.message ? e.message : String(e))); });
    return () => { cancelled = true; clearTimeout(timer); if (window.__gmFailCbs) window.__gmFailCbs.delete(fail); if (roRef.current) roRef.current.disconnect(); };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const g = window.google.maps;
    objsRef.current.forEach((o) => o.setMap(null));
    objsRef.current = [];
    const bounds = new g.LatLngBounds();
    const geom = route?.route?.geometry;
    if (geom && geom.length) {
      const path = geom.map(([lat, lng]) => ({ lat, lng }));
      const line = new g.Polyline({ path, strokeColor: "#4F46E5", strokeWeight: 5, strokeOpacity: 0.9, map });
      objsRef.current.push(line);
      path.forEach((p) => bounds.extend(p));
    }
    if (route?.depot) {
      const m = new g.Marker({ position: { lat: route.depot.lat, lng: route.depot.lng }, map,
        title: `${route.depot.name} \u00b7 ${route.depot.postcode}` });
      objsRef.current.push(m); bounds.extend(m.getPosition());
    }
    if (route?.venue) {
      const m = new g.Marker({ position: { lat: route.venue.lat, lng: route.venue.lng }, map, title: route.venue.name });
      objsRef.current.push(m); bounds.extend(m.getPosition());
    }
    boundsRef.current = bounds.isEmpty() ? null : bounds;
    if (!bounds.isEmpty()) map.fitBounds(bounds, 48);
  }, [route, ready]);

  useEffect(() => {
    if (!trafficRef.current || !mapRef.current) return;
    trafficRef.current.setMap(showTraffic ? mapRef.current : null);
  }, [showTraffic, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const g = window.google.maps;
    incRef.current.forEach((o) => o.setMap(null));
    incRef.current = [];
    if (!infoRef.current) infoRef.current = new g.InfoWindow();
    (prioritised.display || []).forEach((inc) => {
      if (!inc.point) return;
      const m = new g.Marker({
        position: { lat: inc.point.lat, lng: inc.point.lng }, map,
        label: { text: styleFor(inc.kind).emoji, fontSize: "20px" },
        icon: { url: TRANSPARENT_PX, scaledSize: new g.Size(1, 1), labelOrigin: new g.Point(0, 0) },
        opacity: inc.affects ? 1 : 0.6, zIndex: 999,
      });
      m.addListener("click", () => {
        infoRef.current.setContent(incidentPopupHtml(inc));
        infoRef.current.open(map, m);
      });
      incRef.current.push(m);
    });
  }, [incidents, showAll, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const g = window.google.maps;
    vehRef.current.forEach((o) => o.setMap(null));
    vehRef.current = [];
    if (!vehInfoRef.current) vehInfoRef.current = new g.InfoWindow();
    (liveVehicles || []).forEach((v) => {
      if (v.lat == null || v.lng == null) return;
      const m = new g.Marker({
        position: { lat: v.lat, lng: v.lng }, map,
        title: v.registration || v.description || v.vehicleId,
        icon: { url: vanMarkerImageUrl(v), scaledSize: new g.Size(54, 54), anchor: new g.Point(27, 27) },
        zIndex: 800,
      });
      m.addListener("click", () => { vehInfoRef.current.setContent(vehiclePopupHtml(v)); vehInfoRef.current.open(map, m); });
      vehRef.current.push(m);
    });
  }, [liveVehicles, ready]);

  return (
    <div className={className || WRAP} data-testid="task-route-map">
      <div ref={ref} style={{ width: "100%", height: "100%" }} />
      <LiveVehiclesToggle />
      <button onClick={() => setShowTraffic((s) => !s)} data-testid="traffic-toggle"
        className="absolute top-3 right-3 z-10 rounded-lg bg-white/95 border border-slate-200 shadow px-3 py-1.5 text-xs font-semibold text-slate-700 hover:text-indigo-600">
        {showTraffic ? "Live traffic: on" : "Live traffic: off"}
      </button>
      {(incidents || onShowAllChange || providers) && (
        <MapLegend
          counts={prioritised.counts}
          hidden={prioritised.hidden}
          showAll={showAll}
          onToggle={onShowAllChange}
          providers={providers}
          googleTraffic={showTraffic}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leaflet fallback (keyless demo) — no simulated traffic, honest labelling
// ---------------------------------------------------------------------------
function LeafletRouteMap({ route, className, incidents, showAll = false, onShowAllChange, providers, restrictRegistrations }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const vehLayerRef = useRef(null);
  const legendRef = useRef(null);
  const { vehicles, enabled: vansOn, available: vansAvail } = useLiveVehicles();
  const _regSet = buildRegSet(restrictRegistrations);
  const _all = (vansOn && vansAvail) ? vehicles : [];
  const liveVehicles = _regSet ? _all.filter((v) => matchesRegSet(v.registration, _regSet)) : _all;
  const prioritised = prioritiseIncidents(incidents || [], { cap: 12, showAll });

  useEffect(() => {
    if (!ref.current || mapRef.current) return undefined;
    const map = L.map(ref.current, { scrollWheelZoom: false, zoomControl: true });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors", maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    vehLayerRef.current = L.layerGroup().addTo(map);
    map.setView([53.8, -2.5], 6);
    const t = setTimeout(() => map.invalidateSize(), 200);
    return () => {
      clearTimeout(t);
      map.remove();
      mapRef.current = null; layerRef.current = null; legendRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current, layer = vehLayerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    (liveVehicles || []).forEach((v) => {
      if (v.lat == null || v.lng == null) return;
      L.marker([v.lat, v.lng], {
        icon: L.divIcon({ className: "", html: vanDivHtml(v), iconSize: [54, 54], iconAnchor: [27, 27] }),
        zIndexOffset: 800,
      }).addTo(layer).bindPopup(vehiclePopupHtml(v), { maxWidth: 260 }).bindTooltip(v.registration || v.description || "");
    });
  }, [liveVehicles]);

  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    if (legendRef.current) { map.removeControl(legendRef.current); legendRef.current = null; }

    const pts = [];
    const geom = route?.route?.geometry;
    if (geom && geom.length > 1) {
      L.polyline(geom, { color: "#1e293b", weight: 8, opacity: 0.18 }).addTo(layer);
      L.polyline(geom, { color: "#4F46E5", weight: 5, opacity: 0.9 }).addTo(layer);
      pts.push(...geom);
      const legend = L.control({ position: "bottomleft" });
      legend.onAdd = () => {
        const div = L.DomUtil.create("div");
        div.style.cssText = "background:rgba(255,255,255,.92);padding:6px 8px;border-radius:8px;font:11px system-ui;box-shadow:0 1px 4px rgba(0,0,0,.2);line-height:1.4";
        div.innerHTML = '<div style="font-weight:700;color:#334155">Demonstration route</div>'
          + '<div style="color:#94a3b8">Typical drive time \u00b7 live traffic not enabled</div>';
        return div;
      };
      legend.addTo(map);
      legendRef.current = legend;
    } else if (geom && geom.length) {
      L.polyline(geom, { color: "#4F46E5", weight: 4, opacity: 0.85 }).addTo(layer);
      pts.push(...geom);
    } else if (route?.depot && route?.venue) {
      L.polyline([[route.depot.lat, route.depot.lng], [route.venue.lat, route.venue.lng]],
        { color: "#94a3b8", weight: 3, opacity: 0.8, dashArray: "6 8" }).addTo(layer);
    }
    if (route?.depot) {
      L.circleMarker([route.depot.lat, route.depot.lng], {
        radius: 8, color: "#0f172a", weight: 2, fillColor: "#0f172a", fillOpacity: 1,
      }).addTo(layer).bindTooltip(`${route.depot.name} \u00b7 ${route.depot.postcode}`);
      pts.push([route.depot.lat, route.depot.lng]);
    }
    if (route?.venue) {
      L.circleMarker([route.venue.lat, route.venue.lng], {
        radius: 9, color: "#4F46E5", weight: 3, fillColor: "#818CF8", fillOpacity: 1,
      }).addTo(layer).bindTooltip(`${route.venue.name}`);
      pts.push([route.venue.lat, route.venue.lng]);
    }

    if (pts.length > 1) map.fitBounds(pts, { padding: [40, 40] });
    else if (pts.length === 1) map.setView(pts[0], 12);

    (prioritised.display || []).forEach((inc) => {
      if (!inc.point) return;
      L.marker([inc.point.lat, inc.point.lng], { icon: incidentDivIcon(L, inc), zIndexOffset: 1000 })
        .addTo(layer).bindPopup(incidentPopupHtml(inc), { maxWidth: 280 });
    });
    setTimeout(() => map.invalidateSize(), 150);
  }, [route, incidents, showAll]);

  return (
    <div className={`relative ${className || WRAP}`} data-testid="task-route-map-wrap">
      <div ref={ref} data-testid="task-route-map" className="w-full h-full" />
      <LiveVehiclesToggle />
      {(incidents || onShowAllChange || providers) && (
        <MapLegend
          counts={prioritised.counts}
          hidden={prioritised.hidden}
          showAll={showAll}
          onToggle={onShowAllChange}
          providers={providers}
        />
      )}
    </div>
  );
}
